/**
 * SEG-Analyse-Manager — externe WEG-Versammlung vorbereiten
 * Route: /seg/analyse/:propertyId (optional) oder /seg/analysen
 *
 * Ohne KI: Der SEG-Verwalter erfasst TOPs manuell aus der WEG-Einladung und
 * hinterlegt pro TOP eine Empfehlung (Ja/Nein/Enthaltung) für den Sonder-
 * eigentümer. Optionale Vollmachten-Generierung nach § 25 Abs. 3 WEG.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash, FileArrowUp, FileArrowDown, ThumbsUp, ThumbsDown,
  MinusCircle, Question, Buildings, IdentificationCard, FloppyDisk, Handshake,
} from "@phosphor-icons/react";
import { formatDate } from "@/lib/constants";

const REC_META = {
  yes:     { label: "JA (empfohlen)", icon: ThumbsUp, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  no:      { label: "NEIN (empfohlen)", icon: ThumbsDown, cls: "bg-rose-100 text-rose-800 border-rose-200" },
  abstain: { label: "Enthaltung", icon: MinusCircle, cls: "bg-amber-100 text-amber-800 border-amber-200" },
  open:    { label: "offen", icon: Question, cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function SegAnalysen() {
  const { propertyId } = useParams();
  const [analyses, setAnalyses] = useState([]);
  const [segProperties, setSegProperties] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [proxyDialogOpen, setProxyDialogOpen] = useState(false);

  const load = async () => {
    const params = propertyId ? { property_id: propertyId } : {};
    const { data } = await api.get("/seg-analyses", { params });
    setAnalyses(data);
    if (data.length > 0 && !selectedId) setSelectedId(data[0].id);
  };
  useEffect(() => {
    load();
    api.get("/properties?object_type=seg&limit=200")
      .then(({ data }) => setSegProperties((data.items || data || []).filter((p) => p.object_type === "seg")))
      .catch(() => setSegProperties([]));
  }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const active = analyses.find((a) => a.id === selectedId);

  return (
    <div className="space-y-4" data-testid="seg-analysen-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Handshake size={24} weight="bold" /> SEG-Analysen für WEG-Versammlung
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Bereiten Sie den externen WEG-Verwalter-Termin für Ihre Sondereigentümer vor:
            WEG-Einladung hochladen, TOPs manuell erfassen, Empfehlungen dokumentieren,
            Vollmacht (§ 25 Abs. 3 WEG) erzeugen. Ohne KI-Bewertung — Empfehlungen liegen
            in Ihrer fachlichen Beurteilung.
          </p>
        </div>
        <Button onClick={() => setNewDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="seg-new-analysis">
          <Plus size={14} /> Neue Analyse
        </Button>
      </div>

      {analyses.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center text-slate-500">
          <Handshake size={32} className="mx-auto mb-2 text-slate-300" />
          Noch keine SEG-Analysen vorhanden. Legen Sie eine neue Analyse an, um die
          bevorstehende WEG-Versammlung strukturiert vorzubereiten.
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* Sidebar-Liste */}
          <div className="col-span-12 md:col-span-4 lg:col-span-3 bg-white border border-slate-200 rounded-md p-2 space-y-1 max-h-[75vh] overflow-y-auto">
            {analyses.map((a) => (
              <button
                key={a.id}
                onClick={() => setSelectedId(a.id)}
                className={`w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 ${selectedId === a.id ? "bg-slate-100 border border-slate-200" : ""}`}
                data-testid={`seg-item-${a.id}`}
              >
                <div className="text-sm font-medium truncate">{formatDate(a.assembly_date)}</div>
                <div className="text-xs text-slate-500 truncate">
                  {a.weg_property_ref || "WEG unbekannt"} · {(a.agenda_items || []).length} TOPs
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Status: {a.status}
                </div>
              </button>
            ))}
          </div>

          {/* Detail-Editor */}
          <div className="col-span-12 md:col-span-8 lg:col-span-9">
            {active
              ? <AnalysisEditor
                  analysis={active}
                  onSaved={load}
                  onOpenProxy={() => setProxyDialogOpen(true)}
                />
              : <div className="text-slate-400 text-sm">Wählen Sie eine Analyse aus.</div>
            }
          </div>
        </div>
      )}

      <NewAnalysisDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        segProperties={segProperties}
        onCreated={(id) => { setSelectedId(id); load(); }}
      />

      {active && (
        <ProxyDialog
          open={proxyDialogOpen}
          onClose={() => setProxyDialogOpen(false)}
          analysis={active}
          onGenerated={load}
        />
      )}
    </div>
  );
}


function AnalysisEditor({ analysis, onSaved, onOpenProxy }) {
  const [form, setForm] = useState(analysis);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setForm(analysis); setDirty(false); }, [analysis]);

  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const setTop = (idx, k, v) => {
    const items = [...(form.agenda_items || [])];
    items[idx] = { ...items[idx], [k]: v };
    setField("agenda_items", items);
  };
  const addTop = () => {
    const items = [...(form.agenda_items || [])];
    items.push({ number: `TOP ${items.length + 1}`, title: "", recommendation: "open" });
    setField("agenda_items", items);
  };
  const removeTop = (idx) => {
    setField("agenda_items", (form.agenda_items || []).filter((_, i) => i !== idx));
  };

  const save = async () => {
    try {
      await api.patch(`/seg-analyses/${form.id}`, {
        weg_property_ref: form.weg_property_ref,
        assembly_date: form.assembly_date,
        assembly_location: form.assembly_location,
        weg_manager_name: form.weg_manager_name,
        weg_manager_address: form.weg_manager_address,
        agenda_items: form.agenda_items,
        general_notes: form.general_notes,
      });
      setDirty(false);
      toast.success("Gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const uploadInvitation = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/seg-analyses/${form.id}/upload-invitation`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("WEG-Einladung archiviert");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const archive = async () => {
    if (dirty) await save();
    try {
      const { data } = await api.post(`/seg-analyses/${form.id}/archive-analysis`);
      toast.success(`Analyse-PDF archiviert (${data.filename})`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="seg-editor">
      {/* Stammdaten */}
      <div className="grid grid-cols-2 gap-3">
        <F label="Versammlungsdatum *">
          <Input type="date" value={form.assembly_date?.slice(0,10) || ""} onChange={(e) => setField("assembly_date", e.target.value)} />
        </F>
        <F label="Versammlungsort">
          <Input value={form.assembly_location || ""} onChange={(e) => setField("assembly_location", e.target.value)} />
        </F>
        <F label="WEG-Objekt-Referenz">
          <Input value={form.weg_property_ref || ""} placeholder="z. B. WEG Blumenstraße 5" onChange={(e) => setField("weg_property_ref", e.target.value)} />
        </F>
        <F label="Externer WEG-Verwalter">
          <Input value={form.weg_manager_name || ""} placeholder="Firma / Verwalter" onChange={(e) => setField("weg_manager_name", e.target.value)} />
        </F>
        <F label="Anschrift des WEG-Verwalters" className="col-span-2">
          <Textarea rows={2} value={form.weg_manager_address || ""} onChange={(e) => setField("weg_manager_address", e.target.value)} />
        </F>
      </div>

      {/* Aktions-Zeile */}
      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-200">
        <label className="cursor-pointer">
          <input type="file" accept="application/pdf" className="hidden"
                 onChange={(e) => uploadInvitation(e.target.files?.[0])} data-testid="seg-upload-invitation" />
          <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <FileArrowUp size={13} /> WEG-Einladung hochladen
          </span>
        </label>
        {form.invitation_document_id && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${form.invitation_document_id}/download`}
             target="_blank" rel="noreferrer"
             className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5">
            <FileArrowDown size={13} /> Einladung öffnen
          </a>
        )}
        <Button size="sm" variant="outline" onClick={onOpenProxy} className="gap-1.5" data-testid="seg-open-proxy-dialog">
          <IdentificationCard size={13} /> Vollmacht erzeugen
        </Button>
        <Button size="sm" onClick={archive} className="ml-auto bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5" data-testid="seg-archive">
          <FileArrowDown size={13} /> Analyse als PDF archivieren
        </Button>
        <Button size="sm" onClick={save} disabled={!dirty} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="seg-save">
          <FloppyDisk size={13} /> Speichern
        </Button>
      </div>

      {/* TOPs */}
      <div className="pt-3 border-t border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Tagesordnung & Empfehlungen</h3>
          <Button size="sm" variant="outline" onClick={addTop} data-testid="seg-add-top">
            <Plus size={13} className="mr-1" /> TOP hinzufügen
          </Button>
        </div>
        <div className="space-y-3">
          {(form.agenda_items || []).length === 0 && (
            <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
              Noch keine TOPs — bitte aus der WEG-Einladung übertragen.
            </div>
          )}
          {(form.agenda_items || []).map((it, idx) => {
            const meta = REC_META[it.recommendation || "open"];
            const Icon = meta.icon;
            return (
              <div key={idx} className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`seg-top-${idx}`}>
                <div className="grid grid-cols-12 gap-2">
                  <Input className="col-span-2 h-8 text-xs" value={it.number} onChange={(e) => setTop(idx, "number", e.target.value)} placeholder="TOP 1" />
                  <Input className="col-span-7 h-8 text-xs" value={it.title} onChange={(e) => setTop(idx, "title", e.target.value)} placeholder="Titel" />
                  <Select value={it.recommendation || "open"} onValueChange={(v) => setTop(idx, "recommendation", v)}>
                    <SelectTrigger className="col-span-2 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(REC_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => removeTop(idx)} className="col-span-1 text-rose-600">
                    <Trash size={13} />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Textarea rows={2} placeholder="Beschlussantrag (Wortlaut)"
                            value={it.proposal_text || ""}
                            onChange={(e) => setTop(idx, "proposal_text", e.target.value)} />
                  <Textarea rows={2} placeholder="Begründung der Empfehlung"
                            value={it.reasoning || ""}
                            onChange={(e) => setTop(idx, "reasoning", e.target.value)} />
                </div>
                <Textarea rows={1} placeholder="Risiken / Hinweise (Kosten, Rechtsfolgen …)"
                          value={it.risks || ""}
                          onChange={(e) => setTop(idx, "risks", e.target.value)} />
                <div className="flex items-center justify-end">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${meta.cls}`}>
                    <Icon size={10} weight="fill" /> {meta.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Allgemeine Notizen */}
      <F label="Allgemeine Hinweise / Weisungen an den Bevollmächtigten" className="pt-3 border-t border-slate-200">
        <Textarea rows={3} value={form.general_notes || ""} onChange={(e) => setField("general_notes", e.target.value)} />
      </F>
    </div>
  );
}


function NewAnalysisDialog({ open, onClose, segProperties, onCreated }) {
  const [form, setForm] = useState({
    property_id: "", owner_contact_id: "",
    assembly_date: new Date().toISOString().slice(0, 10),
    weg_property_ref: "", assembly_location: "", weg_manager_name: "",
  });
  const [owners, setOwners] = useState([]);

  useEffect(() => {
    if (!form.property_id) { setOwners([]); return; }
    api.get(`/properties/${form.property_id}`)
      .then(({ data }) => {
        const ids = [
          ...(data.owner_contact_ids || []),
          ...(data.owner_contact_id ? [data.owner_contact_id] : []),
        ];
        if (ids.length === 0) { setOwners([]); return; }
        api.get(`/contacts?ids=${ids.join(",")}`).then(({ data: cs }) => setOwners(cs || [])).catch(() => setOwners([]));
      })
      .catch(() => setOwners([]));
  }, [form.property_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.property_id || !form.owner_contact_id || !form.assembly_date) {
      return toast.error("Bitte SEG-Objekt, Eigentümer und Datum wählen");
    }
    try {
      const { data } = await api.post("/seg-analyses", { ...form, agenda_items: [] });
      toast.success("Analyse angelegt");
      onCreated(data.id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="seg-new-dialog">
        <DialogHeader><DialogTitle>Neue SEG-Analyse</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <F label="SEG-Objekt *">
            <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
              <SelectTrigger data-testid="seg-new-property"><SelectValue placeholder="SEG-Objekt wählen …" /></SelectTrigger>
              <SelectContent>
                {segProperties.length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-500">
                    Keine SEG-Objekte vorhanden. Legen Sie ein Objekt mit object_type=seg an.
                  </div>
                )}
                {segProperties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <Buildings size={12} className="inline mr-1" /> {p.property_number} · {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Sondereigentümer *">
            <Select value={form.owner_contact_id} onValueChange={(v) => set("owner_contact_id", v)} disabled={!form.property_id}>
              <SelectTrigger data-testid="seg-new-owner"><SelectValue placeholder="Eigentümer wählen …" /></SelectTrigger>
              <SelectContent>
                {owners.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.display_name || `${o.first_name || ""} ${o.last_name || ""}`.trim() || o.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Versammlungsdatum *">
              <Input type="date" value={form.assembly_date} onChange={(e) => set("assembly_date", e.target.value)} data-testid="seg-new-date" />
            </F>
            <F label="Ort">
              <Input value={form.assembly_location} onChange={(e) => set("assembly_location", e.target.value)} />
            </F>
          </div>
          <F label="WEG-Objekt-Referenz">
            <Input value={form.weg_property_ref} onChange={(e) => set("weg_property_ref", e.target.value)} placeholder="z. B. WEG Blumenstraße 5, 20095 Hamburg" />
          </F>
          <F label="Externer WEG-Verwalter">
            <Input value={form.weg_manager_name} onChange={(e) => set("weg_manager_name", e.target.value)} />
          </F>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="seg-new-submit">Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ProxyDialog({ open, onClose, analysis, onGenerated }) {
  const [form, setForm] = useState({
    proxy_holder_name: "", proxy_holder_address: "",
    valid_for_date: analysis?.assembly_date?.slice(0,10) || "",
    valid_for_all_topics: true, limitations: "",
  });
  useEffect(() => {
    setForm((f) => ({ ...f, valid_for_date: analysis?.assembly_date?.slice(0,10) || "" }));
  }, [analysis?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.proxy_holder_name.trim()) return toast.error("Name des Bevollmächtigten fehlt");
    try {
      const { data } = await api.post(`/seg-analyses/${analysis.id}/generate-proxy`, form);
      toast.success(`Vollmacht erstellt: ${data.filename}`);
      onGenerated();
      onClose();
      // PDF direkt öffnen
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${data.document_id}/download`, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="seg-proxy-dialog">
        <DialogHeader><DialogTitle>Vollmacht zur Vertretung erzeugen</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <F label="Bevollmächtigter (Name) *">
            <Input value={form.proxy_holder_name} onChange={(e) => set("proxy_holder_name", e.target.value)}
                   placeholder="z. B. Domivus GmbH — Herr Müller" data-testid="proxy-holder-name" />
          </F>
          <F label="Anschrift Bevollmächtigter">
            <Textarea rows={2} value={form.proxy_holder_address} onChange={(e) => set("proxy_holder_address", e.target.value)} />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Gültig für Datum">
              <Input type="date" value={form.valid_for_date} onChange={(e) => set("valid_for_date", e.target.value)} />
            </F>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.valid_for_all_topics}
                       onChange={(e) => set("valid_for_all_topics", e.target.checked)} data-testid="proxy-all-topics" />
                für alle TOPs
              </label>
            </div>
          </div>
          <F label="Einschränkungen / Weisungen (optional)">
            <Textarea rows={3} value={form.limitations} onChange={(e) => set("limitations", e.target.value)}
                      placeholder='z. B. „Nur bei einfacher Mehrheit dem Verwalterwechsel zustimmen"' />
          </F>
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            Nach § 25 Abs. 3 WEG. Der Text wird aus einer editierbaren Vorlage generiert;
            die endgültige Datei wird in der Dokumentenmappe (Kategorie &bdquo;Versammlung&ldquo; ›
            &bdquo;SEG-Analysen · Vollmacht&ldquo;) archiviert.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={generate} className="bg-slate-900 hover:bg-slate-800" data-testid="proxy-generate">
            Vollmacht erzeugen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
